import { z } from 'zod';
import { config } from '../config';
import { StorageError } from '../app/errors';
import { MATCH_STATES } from '../domain/machine';
import { isFormationFor } from '../domain/setup';
import {
  ARTIFACT_PHASE,
  ATTRIBUTE_RATINGS,
  ATTRIBUTES,
  MATCH_FORMATS,
  MATCH_PURPOSES,
  POSITIONS,
  SETTING_KEYS,
  TEAM_SHAPE_FIELDS,
} from '../types';
import type { AttributeKey } from '../types';
import { TABLE } from './db';
import type { TableName } from './db';

/**
 * **The repo-boundary runtime validator**, for rows that did not come from this codebase.
 *
 * Design §4 (INV-13): *"Compile-time, plus a repo-boundary runtime validator for imported rows."*
 * §3.5: *"TypeScript enforces the shape at the application boundary; a Zod parse at the repo
 * boundary rejects malformed geometry on import."* BAK-2: *"The import path validates INV-6 and
 * INV-13 at the repo boundary and quarantines violating rows with a surfaced error rather than
 * accepting them (NFR-8)."*
 *
 * ── Why this is not on the write path ────────────────────────────────────────
 *
 * Rows this app creates have been through the compiler and through `domain/`, and the tap path has a
 * 100 ms budget (NFR-1) with two row writes in it. Parsing every row on every increment would spend
 * that budget re-proving something the type system already proved. Rows from a **file** have proved
 * nothing, and that is the boundary this guards: BAK-2 import, SYN-5 restore, and the seeded
 * template library.
 *
 * ── Rejects, does not repair ─────────────────────────────────────────────────
 *
 * A marker at `x = 5` on import is corrupt data, not a fingertip that landed a pixel outside the
 * box — so it is rejected here, while `repos/setPieces.ts` clamps on the live capture path. The two
 * behaviours are deliberate and belong to different callers; collapsing them would either silently
 * accept corruption or reject a legal tap.
 *
 * Build unit U-6 owns the quarantine flow that decides what to *do* with a rejection. This module
 * owns only the verdict.
 */

const id = z.string().min(1);
const ts = z.number().int();
const minute = z.number().int().nonnegative();
const nullable = <T extends z.ZodTypeAny>(inner: T) => inner.nullable();

const observationKind = z.enum(['increment', 'voiceNote', 'flag']);

/**
 * Derived from `domain/machine.ts`'s vocabulary, never re-listed.
 *
 * An earlier version of this file spelled all nine states out again. The `ses-1/match-state-literals`
 * lint rule rejected it, correctly: a second copy of the state list in an import validator is a
 * validator that silently drifts from the enum it is supposed to be checking — an archive carrying a
 * state this file had not been updated for would be rejected as corrupt, or a removed state would
 * keep being accepted. Deriving it means adding a state is a one-line change in one place.
 */
const matchState = z.enum(MATCH_STATES);
const capturePhase = z.enum(['discovery', 'locked']);
const hypothesisPhase = z.enum([
  'inPossession',
  'outOfPossession',
  'attTransition',
  'defTransition',
  'setPiece',
  'individual',
  'gk',
]);
const discoveryBucket = z.enum([
  'inPossession',
  'outOfPossession',
  'attTransition',
  'defTransition',
]);
const statusOverride = z.enum(['disproved', 'invalidated']).nullable();
const status = z.enum(['tracking', 'confirmed', 'disproved', 'invalidated']);
/** Spelled through the constant, so the schema is tied to the declared enum (see `ArtifactPhase`). */
const artifactPhase = z.enum([ARTIFACT_PHASE.halftime, ARTIFACT_PHASE.fullTime]);

/** SP-5 — normalized 0–1 box coordinates at `GEOMETRY_DECIMALS`, or the archive is not byte-stable. */
const boxCoordinate = z
  .number()
  .min(0)
  .max(1)
  .refine(
    (value) => Number(value.toFixed(config.GEOMETRY_DECIMALS)) === value,
    `§11.7: geometry carries at most ${config.GEOMETRY_DECIMALS} decimal places — float formatting varies between engines and one extra digit defeats INV-12’s byte-identity`,
  );

const TeamSchema = z.object({
  id,
  name: z.string().min(1),
  club: nullable(z.string()),
  ageGroup: nullable(z.string()),
  notes: nullable(z.string()),
  createdAt: ts,
});

const PlayerSchema = z.object({
  id,
  teamId: id,
  number: z.number().int().min(1).max(99),
  foot: z.enum(['left', 'right']).nullable(),
  notes: nullable(z.string()),
});

const MatchSchema = z
  .object({
    id,
    teamId: id,
    comp: nullable(z.string()),
    /** `YYYY-MM-DD`. Fixed width, so it sorts lexicographically for FILE-3. */
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'a match date is YYYY-MM-DD, not a timestamp'),
    /**
     * PRE-8 (§6.0a). Nullable because a match exists from the moment an opponent is chosen and the
     * format has no default; **field order is normative** — immediately after `date`, matching
     * `MatchRow` and `MATCH_FIELDS` (§11.7a).
     */
    format: nullable(z.enum(MATCH_FORMATS)),
    /**
     * B §4.1 (F-2). Nullable because a match exists from the moment an opponent is chosen and the
     * purpose has no default; **field order is normative** — immediately after `format`, matching
     * `MatchRow` and `MATCH_FIELDS`. Required-present: an archive this format writes always
     * carries it, and a pre-`v004` archive gets it backfilled at the parse stage
     * (`archive/import.ts`) before any row reaches this schema.
     */
    purpose: nullable(z.enum(MATCH_PURPOSES)),
    halfLengthMin: z.number().int().min(config.HALF_LENGTH_MIN_MIN).max(config.HALF_LENGTH_MIN_MAX),
    formationGuess: nullable(z.string()),
    kickoffTs: nullable(ts),
    halfTs: nullable(ts),
    kickoff2Ts: nullable(ts),
    fullTimeTs: nullable(ts),
    state: matchState,
    capturePhase,
    lockTs: nullable(ts),
    abandoned: z.boolean(),
    lastSyncedTs: nullable(ts),
    lastSyncedHash: nullable(z.string()),
    triagePassedTs: nullable(ts),
  })
  /**
   * **PRE-8's cross-field rule, at the boundary rows actually cross (§6.0a).**
   *
   * Until this existed, `formationGuess` was validated only as `nullable(z.string())` — the list
   * membership lived solely in the command handler, so **an imported archive could already write a
   * formation guess no picker would offer**, and after PRE-8 it could also write one belonging to a
   * different format entirely. A guess and a format that disagree is not a cosmetic mismatch: the
   * picker would show nothing selected while the column held a shape, and the next edit would
   * silently replace a value the user never saw.
   *
   * It also makes §13.3's `v003` backfill **checkable rather than merely argued**: `'11v11'` is the
   * only value under which every guess a pre-`v003` row can hold stays offerable, so any other
   * choice fails the v001 fixture right here. The guard and the backfill prove each other.
   */
  .refine(
    (row) => row.formationGuess === null || isFormationFor(row.format, row.formationGuess),
    'PRE-8: a match’s formation guess must be one its own format offers (§6.0a)',
  );

const HypothesisSchema = z.object({
  id,
  matchId: id,
  text: z.string().min(1),
  phase: hypothesisPhase,
  discoveryBucket,
  playerId: nullable(id),
  statusOverride,
  cachedCount: z.number().int().nonnegative(),
  lastIncrementTs: nullable(ts),
  /**
   * DISC-4's third sort term (§3.2, `v002`). **Non-negative rather than merely an integer**, because
   * `0` is the migration's "predates the column" backfill and a negative value would sort ahead of
   * every genuinely-created row on an imported archive — a board reordered by a corrupt file.
   */
  createdAt: ts.nonnegative(),
  status,
  boardPosition: z.number().int().min(0).max(config.BOARD_CAP - 1).nullable(),
  sourceTemplateId: nullable(id),
  carriedFromMatchId: nullable(id),
  carriedStatus: status.nullable(),
  carriedCount: z.number().int().nonnegative().nullable(),
});

const ObservationSchema = z
  .object({
    id,
    hypothesisId: nullable(id),
    matchId: id,
    matchMinute: minute,
    kind: observationKind,
    audioBlobId: nullable(id),
    createdAt: ts,
  })
  .refine(
    (row) => row.kind !== 'increment' || row.hypothesisId !== null,
    'INV-2: an increment must belong to a hypothesis, or it counts towards nothing',
  )
  .refine(
    (row) => row.kind !== 'flag' || row.hypothesisId === null,
    'FLAG-2: a flag belongs to no hypothesis',
  );

const FlagResolutionSchema = z.object({
  id,
  observationId: id,
  action: z.enum(['promoted', 'dismissed']),
  resolvedAt: ts,
  promotedHypothesisId: nullable(id),
});

const SetPieceSchema = z.object({
  id,
  matchId: id,
  // SP-6 / B-23 — the runtime mirror of `SetPieceType`. Widened by B-23 (free kicks), which is a
  // pure ADDITION: every value an existing stored row can hold is still accepted, so no set-piece
  // row needs rewriting and there is no migration. `type` is not a Dexie index either
  // (`setPieces: 'id, matchId, [matchId+matchMinute]'`), so nothing about the store changed.
  type: z.enum(['defCorner', 'attCorner', 'defFreeKick', 'attFreeKick']),
  matchMinute: minute,
  scheme: z.enum(['zonal', 'man', 'hybrid']).nullable(),
  markers: z.array(
    z.object({
      x: boxCoordinate,
      y: boxCoordinate,
      // Likewise an addition: `wall` and `kickPoint` join SP-2's four and SP-3's two.
      type: z.enum([
        'zonal',
        'man',
        'nearPost',
        'edgeOfBox',
        'target',
        'screener',
        'wall',
        'kickPoint',
      ]),
    }),
  ),
  flags: z.object({
    leftUpTop: z.boolean().optional(),
    edgeMarked: z.boolean().optional(),
    swing: z.enum(['in', 'out']).optional(),
    targetZone: z.enum(['near', 'far', 'edge']).optional(),
  }),
  notes: nullable(z.string()),
  draft: z.boolean(),
});

/** INV-13 as a discriminated union — the runtime mirror of the type. */
const TalkPointSchema = z.discriminatedUnion('phase', [
  z.object({
    id,
    matchId: id,
    phase: z.literal(ARTIFACT_PHASE.fullTime),
    rank: z.number().int().min(1).max(config.TALKPOINT_COUNT),
    instructionText: z.string().min(1),
    sourceHypothesisId: nullable(id),
    attachedSetPieceId: nullable(id),
  }),
  z.object({
    id,
    matchId: id,
    phase: z.literal(ARTIFACT_PHASE.halftime),
    rank: z.number().int().min(1).max(config.TALKPOINT_COUNT),
    instructionText: nullable(z.string()),
    sourceHypothesisId: nullable(id),
    attachedSetPieceId: nullable(id),
  }),
]);

const MatchNoteSchema = z.object({
  id,
  matchId: id,
  phase: artifactPhase,
  text: nullable(z.string()),
  audioBlobId: nullable(id),
  matchMinute: minute,
  createdAt: ts,
  updatedAt: ts,
});

const TemplateSchema = z.object({
  id,
  name: z.string().min(1),
  phase: hypothesisPhase.nullable(),
  hypothesisTexts: z.array(
    z.object({
      text: z.string().min(1),
      phase: hypothesisPhase,
      // PRE-2's CI seed validation asserts this is present on every entry; the design's
      // `discoveryBucket` assignment rule (§3.2) means a hypothesis is never left without a page.
      discoveryBucket,
      slots: z
        .array(z.object({ key: z.string().min(1), kind: z.enum(['shirtNumber', 'foot']) }))
        .optional(),
    }),
  ),
  builtin: z.boolean(),
});

// ── Build unit B-1 — the B data model's repo-boundary schemas (spec §4.1) ────

/** The closed fifteen, derived from `types/` and never re-listed — the `matchState` rule above. */
const position = z.enum(POSITIONS);

/**
 * §3.7.37's sparse rating map, **derived from the one `ATTRIBUTES` table** rather than re-listed.
 *
 * Spelling nineteen keys out again here would be the same defect the `matchState` comment above
 * rejects: a second copy of a vocabulary inside its own validator drifts silently, and an archive
 * carrying an attribute this file had not been updated for would be read as corrupt.
 *
 * Every arm is `.optional()`, because **absence is the only representation of "not assessed"** —
 * a `null` would be a second spelling of it, which §3.7.37 forbids. `.strict()` because an
 * unrecognised attribute key is a *quarantine* finding, not something to strip silently: stripping
 * would accept a row and then export it without the key, breaking BAK-2's byte-stability the same
 * way an accepted unknown `SettingKey` would.
 */
const attributeRating = z.enum(ATTRIBUTE_RATINGS);

const ratingsShape = {} as Record<AttributeKey, z.ZodOptional<typeof attributeRating>>;
for (const attribute of ATTRIBUTES) ratingsShape[attribute.key] = attributeRating.optional();

const playerRatings = z.object(ratingsShape).strict();

/**
 * Our roster (My Team, scenario 2), **reshaped by §15.3**.
 *
 * `jerseyNumber` parses against the same 1–99 band as opponent stubs; `firstName` is non-empty
 * (§3.7.36 requires it, and ruling R-1 keeps it); `primaryPosition` and every member of
 * `secondaryPositions` parse against the closed fifteen; `ratings` is a **sparse** record whose
 * absent keys mean NOT ASSESSED — `z.record` over the closed key enum with `.partial()` semantics
 * gives exactly that, and rejects both an unknown attribute key and an unknown rating value.
 *
 * An archive at `formatVersion <= 3` carries old-shape rows that **every one of these rules would
 * fail**. It is not quarantined row-by-row: §15.6's entity-grain drop removes the whole entity
 * before validation, because fifteen alarming quarantine lines for *the old kind* would be the
 * archive-corruption vocabulary applied to a deliberate act.
 */
const OurPlayerSchema = z.object({
  id,
  jerseyNumber: z.number().int().min(config.SHIRT_NUMBER_MIN).max(config.SHIRT_NUMBER_MAX),
  firstName: z.string().min(1),
  primaryPosition: position,
  secondaryPositions: z.array(position),
  ratings: playerRatings,
  active: z.boolean(),
  createdAt: ts,
  updatedAt: ts,
});

/** A hypothesis annotation (scenario 17). Empty text is rejected — `WriteAnnotation`'s
 *  `ERR_ANNOTATION_EMPTY` is the command-side twin of the same rule (B-2). */
const AnnotationSchema = z.object({
  id,
  matchId: id,
  hypothesisId: id,
  text: z.string().min(1),
  createdAt: ts,
  updatedAt: ts,
});

const adjustmentOrigin = z.enum(['ai', 'manual']);
const adjustmentStatus = z.enum(['suggested', 'accepted', 'rejected']);

/**
 * A coach card (§4.1). `adjustments` is a JSON column read whole (the `setPieces.markers`
 * shape); each entry's `phase` parses against the same seven-value enum as a hypothesis, so
 * an archive carrying an eighth phase is quarantined by name. Entry-size bounds
 * (`COACH_ADJUSTMENT_MAX_CHARS`, `COACH_CARD_MAX_ENTRIES`) are the B-3 command's business —
 * this boundary checks shape, not policy.
 */
const CoachCardSchema = z.object({
  id,
  matchId: id,
  context: artifactPhase,
  status: z.enum(['draft', 'final']),
  selectedHypothesisIds: z.array(id),
  adjustments: z.array(
    z.object({
      entryId: id,
      text: z.string().min(1),
      phase: hypothesisPhase,
      sourceHypothesisIds: z.array(id),
      origin: adjustmentOrigin,
      status: adjustmentStatus,
      aiOriginalText: nullable(z.string()),
      createdAt: ts,
    }),
  ),
  generatedAt: nullable(ts),
  finalizedAt: nullable(ts),
  createdAt: ts,
  updatedAt: ts,
});

/**
 * **The key space is closed, and the import is where that becomes load-bearing.**
 *
 * Build unit U-5 narrowed `SettingRow.key` from `string` to the `SettingKey` union so
 * `archive/fields.ts` could declare a publish-or-deny disposition per key and fail the build on a
 * new one (§11.8). It left this schema parsing a free string, deliberately, because what an
 * unrecognised key should *do* on import is BAK-2's decision. U-6's answer is **quarantine**, and
 * spelling it as `z.enum(SETTING_KEYS)` is that answer:
 *
 *   - accepting it would write a row whose `key` the `SettingRow` type says cannot exist, so every
 *     later reader would be working from a false type;
 *   - `settingDisposition()` denies anything it does not recognise, so an accepted unknown key would
 *     be **dropped on the next export** — export → import → export would not be byte-stable, which
 *     is the one property BAK-2 states outright;
 *   - and the newer client that wrote it is exactly the case §11.8's deny-by-default exists for: a
 *     key whose sensitivity this bundle cannot know must not be published by this bundle.
 *
 * The forward-compatibility cost is bounded and recoverable: a newer client's archive imported into
 * an older one loses that one preference, reported by name, and the user re-sets it in one tap. The
 * alternative — refusing the import — loses the season. (An archive whose `formatVersion` is newer
 * is refused outright in `archive/import.ts`; this covers the case where a key was added without a
 * format bump.)
 *
 * ── Ruling R-3 — the one per-key value refinement, and why it is a `superRefine` ─
 *
 * `value` stays `z.unknown()`. The `teamShape` arm below is a **non-stripping** `superRefine`, not
 * a `z.object`, and that is the whole point: a `z.object` would *drop unknown keys*, so
 * export → import → export would not be byte-stable — the one property this file's paragraph above
 * names as BAK-2's outright statement. A `superRefine` reads the value and reports; it never
 * rewrites it.
 *
 * This puts the first **value** vocabulary into `rowSchemas`, in tension with
 * `domain/commands/settings.ts`'s stated *"no value vocabulary"*. R-3 resolves it: the **command**
 * stays value-agnostic; the **import boundary** is exactly what `rowSchemas` is for. The effect is
 * that a malformed shape off a file is a *named quarantine finding* instead of silence.
 */
const SettingSchema = z
  .object({ key: z.enum(SETTING_KEYS), value: z.unknown() })
  .superRefine((row, ctx) => {
    if (row.key !== 'teamShape') return;

    const value = row.value;
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['value'],
        message: 'teamShape must be an object with the three shape fields and updatedAt.',
      });
      return;
    }

    const record = value as Record<string, unknown>;
    for (const field of TEAM_SHAPE_FIELDS) {
      const expected = field === 'updatedAt' ? 'number' : 'string';
      const actual = record[field];
      const wrong =
        expected === 'number'
          ? typeof actual !== 'number' || !Number.isInteger(actual) || actual < 0
          : typeof actual !== 'string';
      if (wrong) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['value', field],
          message:
            expected === 'number'
              ? `teamShape.${field} must be a non-negative integer timestamp.`
              : `teamShape.${field} must be a string ("" means not recorded).`,
        });
      }
    }
  });

/**
 * The audio row **as an archive carries it** — metadata only.
 *
 * `AudioBlobRow` also holds `blob` and `chunks`, and neither is a JSON field: the bytes are a file
 * inside the zip (`blobs/<id>.<ext>`) and `chunks` is pre-finalize staging (§3.1, `archive/fields`).
 * Zod objects strip unknown keys, so this schema validates a live row and an imported one alike,
 * which is what lets the NFR-7 fixture test check `audioBlobs` against it too.
 *
 * `mime` may legitimately be the empty string: build unit U-1 found `MediaRecorder.mimeType` is `''`
 * before `start()`, so a row created at recording start (§8.2) and never finalized carries no MIME.
 */
const AudioBlobMetaSchema = z.object({
  id,
  mime: z.string(),
  bytes: z.number().int().nonnegative(),
  complete: z.boolean(),
  createdAt: ts,
});

const LogEntrySchema = z.object({
  seq: z.number().int().optional(),
  ts,
  level: z.enum(['debug', 'info', 'warn', 'error']),
  module: z.string().min(1),
  matchId: nullable(id),
  msg: z.string(),
  data: z.record(z.unknown()).nullable(),
});

/**
 * The schema per table. Tables with no schema are **device-local bookkeeping** on VER-7's justified
 * exclusion list (§17.4) — they are never imported, so validating them would be validating something
 * that cannot arrive.
 */
const SCHEMAS: Partial<Record<TableName, z.ZodTypeAny>> = {
  [TABLE.teams]: TeamSchema,
  [TABLE.players]: PlayerSchema,
  [TABLE.matches]: MatchSchema,
  [TABLE.hypotheses]: HypothesisSchema,
  [TABLE.observations]: ObservationSchema,
  [TABLE.flagResolutions]: FlagResolutionSchema,
  [TABLE.setPieces]: SetPieceSchema,
  [TABLE.talkPoints]: TalkPointSchema,
  [TABLE.matchNotes]: MatchNoteSchema,
  [TABLE.annotations]: AnnotationSchema,
  [TABLE.coachCards]: CoachCardSchema,
  [TABLE.ourPlayers]: OurPlayerSchema,
  [TABLE.audioBlobs]: AudioBlobMetaSchema,
  [TABLE.templates]: TemplateSchema,
  [TABLE.settings]: SettingSchema,
  [TABLE.logEntries]: LogEntrySchema,
};

/** Tables an import may carry. Anything else in an archive is a format error, not a row error. */
export const IMPORTABLE_TABLES: readonly TableName[] = Object.keys(SCHEMAS) as TableName[];

/**
 * Parses one row arriving from outside this codebase.
 *
 * @returns the row, typed as `unknown` — the caller casts to the row type it asked for. The value is
 *   returned rather than discarded so the caller writes the *parsed* object, not the raw one.
 * @throws {StorageError} listing every failing field, so BAK-2's quarantine can surface what was
 *   wrong rather than "invalid row" (NFR-8).
 */
export function parseImportedRow(table: TableName, row: unknown): unknown {
  const schema = SCHEMAS[table];
  if (!schema) {
    throw new StorageError(
      `"${table}" is device-local bookkeeping and is never imported; an archive carrying it is malformed.`,
      { context: { table } },
    );
  }
  const parsed = schema.safeParse(row);
  if (!parsed.success) {
    throw new StorageError(`A ${table} row in this archive is not valid and was not imported.`, {
      context: {
        table,
        issues: parsed.error.issues.map((i) => `${i.path.join('.') || '(row)'}: ${i.message}`),
      },
    });
  }
  return parsed.data;
}

/** Non-throwing form, for a caller that wants to quarantine rather than abort (BAK-2). */
export function checkImportedRow(
  table: TableName,
  row: unknown,
): { ok: true; row: unknown } | { ok: false; issues: string[] } {
  try {
    return { ok: true, row: parseImportedRow(table, row) };
  } catch (error) {
    const issues = error instanceof StorageError ? error.context.issues : undefined;
    return {
      ok: false,
      issues: Array.isArray(issues) ? (issues as string[]) : [String(error)],
    };
  }
}
